/**
 * The evidence behind a ticket (YAG-1005): what its error, log and deploy
 * links held when Bailey's spec read them live, which systems were read and
 * when, and what could not be read. It is the record the tracker comment and
 * the agents' pack come from (`GET /api/work-items/:id/evidence`), shown as
 * titles, links and facts; never an excerpt or a log line.
 */
import { useEffect, useState } from "react";
import type { TicketEvidence, TicketEvidenceItem, WorkItem } from "@yagni/shared";
import { Fold } from "../queue/index.js";
import { captureUiError } from "../sentry.js";

/**
 * A ticket a Jira or Linear ticket backs (its own, or a bundle's): the
 * tickets whose spec reads their evidence before it is written.
 */
export function isTrackerBacked(item: Pick<WorkItem, "sourceRef">): boolean {
  const ref = item.sourceRef;
  if (!ref || typeof ref !== "object") return false;
  const source = typeof ref.source === "string" ? ref.source.toLowerCase() : "";
  if ((source === "linear" || source === "jira") && typeof ref.externalId === "string") {
    return ref.externalId.trim() !== "";
  }
  return Array.isArray(ref.linkedTickets) && ref.linkedTickets.length > 0;
}

/**
 * The ticket's evidence, read once per ticket and only when `enabled`; null
 * while there is none, and the moment the ticket changes, so one ticket's
 * evidence never shows on the next.
 */
export function useTicketEvidence(workItemId: string, enabled: boolean): TicketEvidence | null {
  const [evidence, setEvidence] = useState<TicketEvidence | null>(null);
  useEffect(() => {
    setEvidence(null);
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/work-items/${encodeURIComponent(workItemId)}/evidence`, {
          credentials: "include",
        });
        if (res.status === 404) return;
        if (!res.ok) throw new Error(`read_ticket_evidence failed (${res.status})`);
        const body = (await res.json()) as { evidence?: TicketEvidence | null };
        if (!cancelled) setEvidence(body.evidence ?? null);
      } catch (err) {
        captureUiError(err, { area: "Work", operation: "read_ticket_evidence" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workItemId, enabled]);
  return evidence;
}

/** A link only to the web: the record's links are rebuilt from ids, and this keeps it so. */
function webUrl(url: string | null): string | null {
  return url && /^https:\/\//.test(url) ? url : null;
}

function EvidenceLine({ item }: { item: TicketEvidenceItem }) {
  const href = webUrl(item.url);
  return (
    <li data-testid="ticket-evidence-item">
      <strong>{item.source}</strong>:{" "}
      {href ? (
        <a href={href} target="_blank" rel="noreferrer">
          {item.title}
        </a>
      ) : (
        item.title
      )}
      {item.unread ? (
        <> · {item.unread}</>
      ) : (
        <>
          {item.facts.length > 0 && <> · {item.facts.join(" · ")}</>}
          {item.at && (
            <>
              {" "}
              · at <code>{item.at}</code>
            </>
          )}
          {item.changes.length > 0 && (
            <>
              {" "}
              · shipped{" "}
              {item.changes.map((change, i) => {
                const changeHref = webUrl(change.url);
                return (
                  <span key={change.number}>
                    {i > 0 && ", "}
                    {changeHref ? (
                      <a href={changeHref} target="_blank" rel="noreferrer" title={change.title}>
                        #{change.number}
                      </a>
                    ) : (
                      `#${change.number}`
                    )}
                  </span>
                );
              })}
            </>
          )}
          {item.foundBySearch && <> · found by searching for the error the ticket names</>}
        </>
      )}
    </li>
  );
}

/** The evidence, folded under the spec: what was read and when, then one line per item. */
export function TicketEvidenceFold({
  item,
  defaultOpen,
}: {
  item: Pick<WorkItem, "id" | "sourceRef">;
  defaultOpen?: boolean;
}) {
  const evidence = useTicketEvidence(item.id, isTrackerBacked(item));
  if (!evidence) return null;
  return (
    <Fold
      title="Evidence"
      count={evidence.items.length > 0 ? evidence.items.length : undefined}
      defaultOpen={defaultOpen}
    >
      <div data-testid="ticket-evidence">
        {evidence.summary && <p className="body-p">{evidence.summary}</p>}
        {evidence.items.length > 0 && (
          <div className="md-body brief">
            <ul>
              {evidence.items.map((item, i) => (
                <EvidenceLine key={`${item.source}:${item.url ?? item.title}:${i}`} item={item} />
              ))}
            </ul>
          </div>
        )}
      </div>
    </Fold>
  );
}
